import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useTypewriter } from '../hooks/useTypewriter';
import { useNetworkInfo } from '../hooks/useNetworkInfo';
import videoHero from '../images/hero.mp4';
// Odporúčanie: vytvor ľahší variant videa pre pomalé pripojenia (napr. hero-low.mp4, 720p, silnejšia kompresia)
// a odkomentuj import nižšie.
// import videoHeroLow from '../images/hero-low.mp4';
import image2 from '../images/barvinokvonzima.png';
import heroLogo from '../images/herologo.png';
import alternativeHero from '../images/alternative.jpg';
import RatingWidget from '../components/RatingWidget';
import LazyVideo from '../components/LazyVideo';
import LazyImage from '../components/LazyImage';

const Home = () => {
  const { t, language } = useLanguage();
  const typewriterText = t('home.subtitle');
  const { displayedText } = useTypewriter(typewriterText, 100, 1000, 3000, 50);
  const { isSlowConnection, isSupported, effectiveType } = useNetworkInfo();

  // Stav pre fallback podľa času načítania videa (pre prehliadače bez Network Information API)
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [fallbackByTimeout, setFallbackByTimeout] = useState(false);
  const [forceImageHero, setForceImageHero] = useState(false);

  // Ak API funguje, spoliehame sa naň; ak nie, po 5s bez načítaného videa použijeme obrázok
  useEffect(() => {
    if (isSupported) {
      // Pri prehliadačoch s podporou sa riadime iba network info
      setFallbackByTimeout(false);
      setVideoLoaded(false);
      return;
    }

    if (videoLoaded) {
      // Video sa načítalo včas, netreba fallback
      setFallbackByTimeout(false);
      return;
    }

    const timer = setTimeout(() => {
      setFallbackByTimeout(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isSupported, videoLoaded]);

  // Ochrana pre slabšie zariadenia (hlavne Edge na notebookoch).
  // Nezasahuje do existujúcej štruktúry, len pridá "force image" fallback.
  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isEdge = /Edg\//.test(ua);
    const cores = navigator.hardwareConcurrency || 8;
    const memory = navigator.deviceMemory || 8;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (isEdge && (cores <= 4 || memory <= 4 || prefersReducedMotion)) {
      setForceImageHero(true);
    }
  }, []);

  // Hero fotka sa použije pre všetko, čo nie je 4g (2g/3g/slow-2g), ak API vie zmerať pripojenie.
  // Navyše, ak API nie je podporované a video sa do 5 sekúnd nenačíta, prepneme na obrázok.
  const isSub4G = effectiveType !== '4g';
  const useImageHeroByNetwork = isSupported && isSub4G;
  const useImageHeroByTimeout = !isSupported && fallbackByTimeout;
  const useImageHero = useImageHeroByNetwork || useImageHeroByTimeout || forceImageHero;

  const [aboutRef, aboutVisible] = useScrollAnimation();
  const [reviewsRef, reviewsVisible] = useScrollAnimation();
  const [galleryRef, galleryVisible] = useScrollAnimation();

  return (
    <>
      <Helmet>
        <title>Chata Barvinok – Ubytovanie Litmanová</title>
        <meta
          name="description"
          content="Štýlová chata Barvinok pri vleku v Litmanovej. 4 spálne, 2 krby, 3 kúpeľne, Wi‑Fi – ideálna pre rodiny a skupiny počas celého roka."
        />
      </Helmet>
      <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Media - video pre rýchle pripojenia, obrázok pre pomalé */}
        <div className="absolute inset-0">
          {useImageHero ? (
            <LazyImage
              src={alternativeHero}
              alt="Chata Barvinok hero"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <LazyVideo
              src={videoHero}
              poster={alternativeHero}
              // lowQualitySrc={videoHeroLow} // po vytvorení menšieho videa môžeš túto vetu odkomentovať
              autoPlay
              loop
              muted
              playsInline
              eager={true}
              className="absolute inset-0 w-full h-full object-cover"
              onLoadedData={() => setVideoLoaded(true)}
              onError={() => setForceImageHero(true)}
            />
          )}
          {/* Dark Overlay - stronger on mobile for better text readability */}
          <div className="absolute inset-0 bg-black/40 md:bg-black/30"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-16 sm:pt-20 pb-8">
          <div className="mb-0 flex justify-center">
            <LazyImage 
              src={heroLogo} 
              alt="Chata Barvinok Logo" 
              className="h-48 md:h-64 lg:h-80 xl:h-96 w-auto object-contain"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>
          
          {/* Typewriter text - nahradil pôvodný subtitle */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-6 sm:mb-8 md:mb-10 max-w-4xl mx-auto leading-relaxed px-2 -mt-4 sm:-mt-6 whitespace-nowrap overflow-hidden">
            {displayedText}
            <span className="inline-block w-0.5 h-4 sm:h-5 md:h-6 bg-gray-200 animate-pulse ml-1">|</span>
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4">
            <Link
              to="/ubytovanie#reservation-section"
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-copper-500 text-white font-semibold hover:bg-copper-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 uppercase tracking-wide text-sm sm:text-base"
            >
              {t('home.reserve')}
            </Link>
            <Link
              to="/ubytovanie"
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-transparent text-white font-semibold border-2 border-white hover:bg-white/10 transition-all uppercase tracking-wide text-sm sm:text-base"
            >
              {t('home.learnMore')}
            </Link>
          </div>
        </div>
      </section>

      {/* O Chate Section */}
      <section id="about-section" className="py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            ref={aboutRef}
            className={`text-center mb-16 transition-all duration-800 ${aboutVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
          >
            <span className="text-copper-400 text-sm font-semibold uppercase tracking-widest mb-4 block">
              {t('home.about')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {t('home.aboutTitle')}
            </h2>
            <div className="w-24 h-1 bg-copper-500 mx-auto"></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div 
              className={`space-y-6 transition-all duration-800 delay-200 ${aboutVisible ? 'animate-fade-in-left opacity-100' : 'opacity-0'}`}
            >
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('home.aboutSubtitle')}
              </h3>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                {t('home.aboutText1')}
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                {t('home.aboutText2')}
              </p>
              
              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-copper-400 mb-2">100%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">{t('home.satisfaction')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-copper-400 mb-2">24/7</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">{t('home.available')}</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-copper-400 mb-2">1+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">{t('home.years')}</div>
                </div>
              </div>
            </div>

            <div 
              className={`relative transition-all duration-800 delay-300 ${aboutVisible ? 'animate-fade-in-right opacity-100' : 'opacity-0'}`}
            >
              <div className="absolute -inset-4 bg-copper-500/20 rounded-2xl blur-2xl"></div>
              <LazyImage 
                src={image2} 
                alt="Chata Barvinok interiér" 
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section 
        ref={reviewsRef}
        className={`py-24 bg-gray-50 dark:bg-gray-800 transition-all duration-800 ${reviewsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-copper-400 text-sm font-semibold uppercase tracking-widest mb-4 block">
              {t('home.reviews')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {t('home.reviews')}
            </h2>
            <div className="w-24 h-1 bg-copper-500 mx-auto mb-4"></div>
          </div>

          <div className={`max-w-6xl mx-auto transition-all duration-800 delay-200 ${reviewsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
            <RatingWidget />
          </div>
        </div>
      </section>

      {/* Quick Gallery Preview */}
      <section 
        ref={galleryRef}
        className={`py-24 bg-gray-100 dark:bg-black transition-all duration-800 ${galleryVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-copper-400 text-sm font-semibold uppercase tracking-widest mb-4 block">
              {t('home.gallery')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {t('home.galleryTitle')}
            </h2>
            <div className="w-24 h-1 bg-copper-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
              {t('home.gallerySubtitle')}
            </p>
            <Link
              to="/galeria"
              className="inline-block px-8 py-4 bg-copper-500 text-white font-semibold hover:bg-copper-600 transition-all uppercase tracking-wide"
            >
              {t('home.gallery')}
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;

